// src/pages/GeneratorPage.js
import { DashboardGeneratorHtml } from "../../components/skins/dashboard-generator.ts";
import { globalState } from "../../framework/globalState.ts";
import { subscribeRegister, sendCommand } from "../../services/mqttClient.ts";
import { FullscreenToggle } from "../../components/utils/fullscreen-toggle.ts";
import { RechartsChartManager } from "../../components/charts/RechartsChartManager.tsx";
import { chartStyles } from "../../components/charts/ChartStyles.ts";
import { groupByDictionaryKey, applyThreshold } from '../../services/registerUtils.ts';
import { GaugeMeterManager } from "../../components/charts/GaugeMeterManager.ts";
import {
  applySummaryThresholdToElement,
  clearThresholdIndicator,
  computeAggregateFromMap,
  type SummaryThresholdSpec,
} from '../../services/summaryThreshold.ts';

export default function GeneratorPage() {
  console.log("[GeneratorPage] Initializing...");

  const container = document.createElement("div");
  container.classList.add("parent-container");

  try {
    container.innerHTML = new DashboardGeneratorHtml().generatorskin().trim();
    console.log("[GeneratorPage] Skin loaded");
  } catch (error) {
    console.error("[GeneratorPage] Skin error:", error);
    container.innerHTML = "<p>Error loading dashboard skin</p>";
    return container;
  }

  const siteWithData = globalState.currentSiteWithData;
  const site = (siteWithData as any)?.site || siteWithData;
  const registers = ((siteWithData as any)?.devices || []).flatMap((d: any) =>
        (d.registers || []).map((r: any) => ({ ...r, deviceName: d.name, deviceId: d._id }))
      );

  const subscriptions = [];

  if (!site?._id) {
    console.warn("[GeneratorPage] No site selected");
    container.innerHTML += "<p class='text-center text-danger'>No site selected.</p>";
    return container;
  }

  console.log(`[GeneratorPage] Site: ${site._id} (${site.name || 'Unnamed'})`);


  // === Confirm Modal ===
  const modal = document.createElement("div");
  modal.className = "confirm-modal";
  modal.innerHTML = `
    <div class="modal-content">
      <h3>Confirm Action</h3>
      <p id="confirm-message">Are you sure?</p>
      <div class="actions">
        <button id="confirm-yes" class="confirm-yes">Yes</button>
        <button id="confirm-no" class="confirm-no">Cancel</button>
      </div>
    </div>
  `;
  container.appendChild(modal);

  function showConfirm(message, callback) {
    const msg = modal.querySelector("#confirm-message");
    const yes = modal.querySelector("#confirm-yes");
    const no = modal.querySelector("#confirm-no");
    if (!msg || !yes || !no) return;
    msg.textContent = message;
    modal.classList.add("show");
    const yesClone = yes.cloneNode(true);
    const noClone = no.cloneNode(true);
    yes.replaceWith(yesClone);
    no.replaceWith(noClone);
    yesClone.onclick = () => { modal.classList.remove("show"); callback(true); };
    noClone.onclick = () => { modal.classList.remove("show"); callback(false); };
  }

  // === DG UNIT DETECTION ===
  const dgGroups = groupByDictionaryKey(registers);
  const dgConfigKeys = Object.keys(dgGroups).filter(k => k.startsWith('diesel_'));
  const dgInstances = new Set<number>();

  dgConfigKeys.forEach(key => {
    dgGroups[key]?.forEach(g => {
      const instance = Number((g as any).instanceNumber);
      dgInstances.add(Number.isFinite(instance) && instance > 0 ? instance : 1);
    });
  });

  const dgUnits = Array.from(dgInstances)
    .sort((a, b) => a - b)
    .map((instanceNumber, i) => ({ id: `DG ${i + 1}`, instanceNumber }));

  if (dgUnits.length === 0) dgUnits.push({ id: "DG 1", instanceNumber: 1 });

  console.log(`[GeneratorPage] DG Units:`, dgUnits.map(u => u.id).join(", "));

  // === REMOTE CONTROL SECTION ===
  const remoteControlSection = container.querySelector(".remote-control");
  if (!remoteControlSection) {
    console.error("[GeneratorPage] .remote-control not found");
  } else {
    console.log("[GeneratorPage] Building remote control table");

    const remoteTable = remoteControlSection.querySelector("table");
    if (!remoteTable) {
      console.error("[GeneratorPage] Table missing");
    } else {
      const thead = remoteTable.querySelector("thead tr");
      const tbody = remoteTable.querySelector("tbody");
      if (!tbody) {
        console.error("[GeneratorPage] Table body missing");
      } else {
      const tbodyRows = tbody.querySelectorAll("tr");

      const metricOrder = [
        "diesel_active_power",
        "diesel_reactive_power",
        "diesel_frequency",
        "diesel_voltage",
        "diesel_current",
        "diesel_fuel_level",
      ];

      // === 1. STATUS ROW + ICONS ===
      const statusRow = document.createElement("tr");
      const statusLabelTd = document.createElement("td");
      statusLabelTd.colSpan = 2;
      statusLabelTd.textContent = "Status";
      statusRow.appendChild(statusLabelTd);

      const statusCells = {};
      const dgStatus = {};

      dgUnits.forEach((unit, idx) => {
        const td = document.createElement("td");
        td.className = "text-center w-100 bg-subcontainer";
        td.innerHTML = '<span class="status-icon" data-status="unknown"></span>';
        statusRow.appendChild(td);
        const instanceKey = String(unit.instanceNumber);
        statusCells[instanceKey] = td.querySelector(".status-icon");
        dgStatus[instanceKey] = null;
      });
      tbody.insertBefore(statusRow, tbodyRows[0]);

      // === 2. METRIC LABELS ===
      tbodyRows.forEach((row, i) => {
        const labelTd = row.children[0];
        if (labelTd && metricOrder[i]) {
          const configKey = metricOrder[i];
          const display = configKey.replace(/^diesel_/, '').replace(/_/g, ' ');
          labelTd.textContent = display;
          labelTd.setAttribute("data-metric", display);
        }
      });

      // === 3. ADD DG COLUMNS ===
      dgUnits.slice(1).forEach(unit => {
        const th = document.createElement("th");
        th.textContent = unit.id;
        thead.appendChild(th);
        tbody.querySelectorAll("tr").forEach(row => {
          const td = document.createElement("td");
          td.className = "text-right w-100 bg-subcontainer";
          td.textContent = "0.0";
          row.appendChild(td);
        });
      });

      // === 4. CONTROL ROWS ===
      const controlRow = document.createElement("tr");
      const controlLabelTd = document.createElement("td");
      controlLabelTd.colSpan = 2;
      controlLabelTd.textContent = "Generator Control";
      controlRow.appendChild(controlLabelTd);
      const controlTd = document.createElement("td");
      controlTd.className = "text-left";
      controlRow.appendChild(controlTd);

      const emergencyRow = document.createElement("tr");
      const emergencyLabelTd = document.createElement("td");
      emergencyLabelTd.colSpan = 2;
      emergencyLabelTd.textContent = "Safety Control";
      emergencyRow.appendChild(emergencyLabelTd);
      const emergencyTd = document.createElement("td");
      emergencyTd.className = "text-left";
      emergencyRow.appendChild(emergencyTd);

      const startStopButtons = {};
      const emergencyButtons = {};

      dgUnits.forEach((unit, idx) => {
        const startStopBtn = document.createElement("button");
        startStopBtn.dataset.dgIndex = idx;
        startStopBtn.className = "generator-toggle";
        updateStartStopButton(startStopBtn, null);
        startStopButtons[String(unit.instanceNumber)] = startStopBtn;
        controlTd.appendChild(startStopBtn);

        const emergencyBtn = document.createElement("button");
        emergencyBtn.textContent = `Emergency Stop ${unit.id}`;
        emergencyBtn.dataset.action = "emergency-stop";
        emergencyBtn.dataset.dgIndex = idx;
        emergencyBtn.className = "emergency-stop";
        emergencyButtons[String(unit.instanceNumber)] = emergencyBtn;
        emergencyTd.appendChild(emergencyBtn);

        if (idx < dgUnits.length - 1) {
          const spacer = document.createElement("span");
          spacer.style.marginRight = "8px";
          controlTd.appendChild(spacer.cloneNode());
          emergencyTd.appendChild(spacer.cloneNode());
        }
      });

      tbody.appendChild(controlRow);
      tbody.appendChild(emergencyRow);

      // === 5. UPDATE BUTTON TEXT ===
      function updateStartStopButton(btn, status) {
        const dgIndex = parseInt(btn.dataset.dgIndex);
        const unit = dgUnits[dgIndex];
        if (status === 1) {
          btn.textContent = `Stop ${unit.id}`;
          btn.dataset.action = "generator-stop";
          btn.classList.remove("btn-start");
          btn.classList.add("btn-stop");
        } else if (status === 0) {
          btn.textContent = `Start ${unit.id}`;
          btn.dataset.action = "generator-start";
          btn.classList.remove("btn-stop");
          btn.classList.add("btn-start");
        } else {
          btn.textContent = `N/A`;
          btn.dataset.action = "";
          btn.disabled = true;
        }
      }

      // === 6. STATUS SUBSCRIPTION ===
      const statusRegisters = registers.filter((r: any) =>
        String(r.dictionaryKey || '').toLowerCase() === 'dg_status' ||
        String(r.dictionaryKey || '').toLowerCase() === 'diesel_status'
      );

      statusRegisters.forEach(reg => {
        const instanceKey = String(Number((reg as any).instanceNumber) || 1);
        const iconEl = statusCells[instanceKey] || statusCells["1"];
        const btn = startStopButtons[instanceKey] || startStopButtons["1"];
        if (!iconEl || !btn) return;

        const raw = reg.value ?? null;
        dgStatus[instanceKey] = raw;

        if (raw === null) {
          iconEl.dataset.status = "unknown";
          iconEl.className = "status-icon status-unknown";
          updateStartStopButton(btn, null);
        } else if (raw === 1) {
          iconEl.dataset.status = "running";
          iconEl.className = "status-icon status-running";
          updateStartStopButton(btn, 1);
        } else if (raw === 0) {
          iconEl.dataset.status = "stopped";
          iconEl.className = "status-icon status-stopped";
          updateStartStopButton(btn, 0);
        } else {
          iconEl.dataset.status = "fault";
          iconEl.className = "status-icon status-fault";
          updateStartStopButton(btn, null);
        }
      });

      // Force immediate summary update
      requestAnimationFrame(() => {
        updateSummary();
      });

      statusRegisters.forEach(reg => {
        const instanceKey = String(Number((reg as any).instanceNumber) || 1);
        const iconEl = statusCells[instanceKey] || statusCells["1"];
        const btn = startStopButtons[instanceKey] || startStopButtons["1"];

        if (!iconEl || !btn) return;

        const unsub = subscribeRegister(site._id, reg.deviceId, reg._id, payload => {
          const raw = payload?.value ?? null;
          dgStatus[instanceKey] = raw;

          if (raw === null) {
            iconEl.dataset.status = "unknown";
            iconEl.className = "status-icon status-unknown";
            updateStartStopButton(btn, null);
          } else if (raw === 1) {
            iconEl.dataset.status = "running";
            iconEl.className = "status-icon status-running";
            updateStartStopButton(btn, 1);
          } else if (raw === 0) {
            iconEl.dataset.status = "stopped";
            iconEl.className = "status-icon status-stopped";
            updateStartStopButton(btn, 0);
          } else {
            iconEl.dataset.status = "fault";
            iconEl.className = "status-icon status-fault";
            updateStartStopButton(btn, null);
          }
        });
        subscriptions.push(unsub);
      });

      // === 7. BUTTON HANDLERS ===
      remoteControlSection.querySelectorAll("button[data-action]").forEach(btn => {
        btn.addEventListener("click", () => {
          const action = (btn.dataset.action || "").trim();
          if (!action) return;

          const dgIndex = parseInt(btn.dataset.dgIndex) || 0;
          const targetUnit = dgUnits[dgIndex];
          const targetInstance = Number(targetUnit.instanceNumber) || 1;

          const controlRegister = registers.find((r: any) =>
            String(r.dictionaryKey || '').toLowerCase() === 'diesel_control' &&
            (Number(r.instanceNumber) || 1) === targetInstance,
          ) || registers.find((r: any) =>
            String(r.dictionaryKey || '').toLowerCase() === 'dg_meter_control' &&
            (Number(r.instanceNumber) || 1) === targetInstance,
          );
          if (!controlRegister) {
            console.error(`[GeneratorPage] Control register not found for instance ${targetInstance}`);
            alert("Control register not configured");
            return;
          }

          const isEmergency = action.includes("emergency");
          const message = isEmergency
            ? `Emergency Stop ${targetUnit.id}?`
            : `${action === 'generator-start' ? 'Start' : 'Stop'} ${targetUnit.id}?`;

          showConfirm(message, confirmed => {
            if (!confirmed) return;

            const payload = {
              action: isEmergency ? "emergency-stop" : action,
              siteId: site._id,
              registerAddress: controlRegister.address,
              timestamp: new Date().toISOString(),
              user: globalState.user?.username || "system"
            };

            console.log(`[GeneratorPage] Sending:`, payload);

            try {
              sendCommand(`site/${site._id}/generator/control`, payload);
              btn.disabled = true;
              const orig = btn.textContent;
              btn.textContent = "Sending...";
              setTimeout(() => {
                btn.disabled = false;
                btn.textContent = orig;
              }, 2000);
            } catch (err) {
              console.error("[GeneratorPage] Command failed:", err);
              alert("Failed to send command");
            }
          });
        });
      });
      }
    }
  }

  // === SUMMARY ELEMENTS ===
  const summaryElements = {
    activePower: container.querySelector('[data-register="DG Meter Active Power"]'),
    frequency: container.querySelector('[data-register="DG Meter Frequency"]'),
    voltage: container.querySelector('[data-register="DG Meter Voltage Phase A"]'),
    current: container.querySelector('[data-register="DG Meter Current Phase A"]'),
    oilPressure: container.querySelector('[data-register="DG Meter Oil Pressure"]'),
    fuelLevel: container.querySelector('[data-register="DG Meter Fuel Level"]'),
  };

  const summaryMapping = {
    activePower: 'diesel_active_power',
    frequency: 'diesel_frequency',
    voltage: 'diesel_voltage',
    current: 'diesel_current',
    oilPressure: 'diesel_oil_pressure',
    fuelLevel: 'diesel_fuel_level',
  };

  const summarySpec: SummaryThresholdSpec = {
    activePower: { keys: ['DIESEL_ACTIVE_POWER'], mode: 'sum' },
    frequency: { keys: ['DIESEL_FREQUENCY'], mode: 'avg' },
    voltage: { keys: ['DIESEL_VOLTAGE'], mode: 'avg' },
    current: { keys: ['DIESEL_CURRENT'], mode: 'avg' },
    oilPressure: { keys: ['DIESEL_OIL_PRESSURE'], mode: 'avg' },
    fuelLevel: { keys: ['DIESEL_FUEL_LEVEL'], mode: 'avg' },
  };

  const summaryValueMode: Record<string, 'sum' | 'avg'> = {
    activePower: 'sum',
    frequency: 'avg',
    voltage: 'avg',
    current: 'avg',
    oilPressure: 'avg',
    fuelLevel: 'avg',
  };

  const summaryValues = {};

  // ========================================
  // 1. INSTANT INITIAL VALUES (REAL DATA ON LOAD!)
  // ========================================
  dgConfigKeys.forEach(configKey => {
    if (!summaryValues[configKey]) summaryValues[configKey] = {};
    const group = dgGroups[configKey] || [];
    group.forEach(reg => {
      if (reg.value == null) return;
      const value = reg.value as number;
      summaryValues[configKey][reg.name] = value;
      const idx = String(Number((reg as any).instanceNumber) || 1);
      const metricName = configKey.replace(/^diesel_/, '').replace(/_/g, ' ');
      const row = container.querySelector(`tr:has(td[data-metric="${metricName}"])`);
      if (row) {
        const dgIndex = dgUnits.findIndex((u: any) => String(u.instanceNumber) === idx);
        if (dgIndex < 0) return;
        const targetCell = row.children[2 + dgIndex] as HTMLElement;
        if (targetCell) {
          targetCell.textContent = value.toFixed(2);
          applyThreshold(targetCell, value, reg.meta);
        }
      }
    });
  });

  function updateSummary() {
    Object.entries(summaryMapping).forEach(([key, configKey]) => {
      const el = summaryElements[key] as HTMLElement;
      if (!el) return;
      const group = dgGroups[configKey] || [];
      const value = computeAggregateFromMap(group as any[], summaryValues[configKey] || {}, summaryValueMode[key] || 'sum');
      if (value !== null) {
        el.textContent = value.toFixed(2);
        applySummaryThresholdToElement(el, key, value, registers as any[], summarySpec);
      } else {
        el.textContent = "N/A";
        clearThresholdIndicator(el);
      }
    });
  }

  // === Subscribe to All DG Registers ===
  console.log(`[GeneratorPage] Subscribing to ${dgConfigKeys.length} metric groups`);
  dgConfigKeys.forEach(configKey => {
    const group = dgGroups[configKey] || [];
    if (!summaryValues[configKey]) summaryValues[configKey] = {};
    group.forEach(reg => {
      const unsub = subscribeRegister(site._id, reg.deviceId, reg._id, payload => {
        const raw = payload?.value ?? null;
        const value = typeof raw === 'number' ? raw : null;
        summaryValues[configKey][reg.name] = value;
        const idx = String(Number((reg as any).instanceNumber) || 1);
        const metricName = configKey.replace(/^diesel_/, '').replace(/_/g, ' ');
        const row = container.querySelector(`tr:has(td[data-metric="${metricName}"])`);
        if (row && value !== null) {
          const dgIndex = dgUnits.findIndex((u: any) => String(u.instanceNumber) === idx);
          if (dgIndex < 0) return;
          const targetCell = row.children[2 + dgIndex] as HTMLElement;
          if (targetCell) {
            targetCell.textContent = value.toFixed(2);
            applyThreshold(targetCell, value, reg.meta);
          }
        }
        updateSummary();
      });
      subscriptions.push(unsub);
    });
  });

  console.log("[GeneratorPage] Initializing charts");
  const chartManager = new RechartsChartManager(site, registers);
  const gmManager = new GaugeMeterManager(site, registers);
  // --------------------------------------------------------------
  // 1. Defer chart creation until the next animation frame
  // --------------------------------------------------------------
  requestAnimationFrame(() => {
    console.log("[GeneratorPage] DOM ready – creating charts");
    initAllCharts();
  });

  function initAllCharts() {
    // ----- Power vs Frequency -----
    chartManager.createChart(
      "engines_power_vs_frequency",
      [],
      { ...chartStyles.enginePFLayout, showControls: true }
    );
    chartManager.bindRegisterToChart("engines_power_vs_frequency", [
      { configKey: "diesel_active_power", mode: "sum", name: "Total Active Power" },
      { configKey: "diesel_frequency",   mode: "average", name: "Avg. Frequency", line: { color: "#ff8800" } }
    ]);

    // ----- Fuel-level datatable -----
    chartManager.createChart(
      "engine_fuel_level_datatable",
      [],
      { ...chartStyles.fuelLevelLayout, showControls: true }
    );
    chartManager.bindRegisterToChart("engine_fuel_level_datatable", [
      { configKey: "diesel_fuel_level", mode: "multi", name: "Fuel Level" }
    ]);

    // ----- GAUGE (fixed) -----
    // gmManager.createVisual("engine_fuel_gauge", "gauge", [
    //   { configKey: "diesel_fuel_level", mode: "average", name: "Fuel Level", scale: 1 }
    // ]);

    gmManager.createVisual("engine_fuel_gauge", "gauge", [
      { configKey: "diesel_fuel_level", mode: "average", name: "Fuel Level", unit: "%", adaptive: true }
    ]);


    // ----- Active Power -----
    chartManager.createChart(
      "engines_active_power",
      [],
      { ...chartStyles.activePowerLayout, showControls: true }
    );
    chartManager.bindRegisterToChart("engines_active_power", [
      { configKey: "diesel_active_power", mode: "sum", name: "Total Active Power" }
    ]);

    // ----- Reactive Power -----
    chartManager.createChart(
      "engine_reactive_power",
      [],
      { ...chartStyles.reactivePowerLayout, showControls: true }
    );
    chartManager.bindRegisterToChart("engine_reactive_power", [
      { configKey: "diesel_reactive_power", mode: "sum", name: "Total Reactive Power" }
    ]);
  }

  // === INIT & CLEANUP ===
  requestAnimationFrame(() => {
    console.log("[GeneratorPage] Initializing fullscreen toggle");
    new FullscreenToggle().init();
  });

  container.cleanup = () => {
    console.log(`[GeneratorPage] Cleaning up ${subscriptions.length} subscriptions`);
    subscriptions.forEach(unsub => typeof unsub === "function" && unsub());
    chartManager.cleanup();
    gmManager.cleanup();
  };

  console.log("[GeneratorPage] Initialization complete");
  return container;
}